import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';
import { UsaStates } from 'usa-states';
import withWidth from '@material-ui/core/withWidth';

import { Field, getFormValues } from 'redux-form';

import Layout from 'components/common/Layout';
import Grid from '@material-ui/core/Grid';
import Progress from 'components/common/Progress';
import Filter from 'components/common/Filter';
import TabNavigation from 'components/common/TabNavigation';
import HCPCSCodesTable from 'components/settings/HCPCSCodesTable';
import HCPCSCodeCreateEditDialog from 'components/settings/HCPCSCodeCreateEditDialog';
import UploadDialog from 'components/settings/UploadDialog';
import Menu, { MenuItem } from 'components/common/Menu';
import SelectAutosuggestField from 'components/fields/SelectAutosuggestField';

import {
  fetchHCPCSCodes,
  clearHCPCSCodes,
} from 'redux/modules/hcpcsCodes';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';

import { appTitle, titleSeparator } from 'config/helmetSettings';

const usaStates = new UsaStates();

class DataPortabilityHCPCSCodes extends Component {
  constructor(props) {
    super(props);

    this.state = {
      createDialogOpen: false,
      anchorEl: null,
      uploadDialogOpen: false,
      route: null,
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onFilterPrimaryButtonClick = this.onFilterPrimaryButtonClick.bind(this);
    this.onFilterSecondaryButtonClick = this.onFilterSecondaryButtonClick.bind(this);
    this.onCreateDialogClose = this.onCreateDialogClose.bind(this);
    this.onUploadDialogClose = this.onUploadDialogClose.bind(this);
    this.onMenuClose = this.onMenuClose.bind(this);
    this.onMenuItemClick = this.onMenuItemClick.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearHCPCSCodes();
  }

  componentDidMount() {
    this.onRequest();
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width } = this.props;
    const { offset } = this.state;
    const filtersChanged = nextProps.filters['HCPC S Code'] !== filters['HCPC S Code']
    || nextProps.filters.State !== filters.State;
    let newOffset = offset;
    if (filtersChanged) {
      newOffset = 0;
    }

    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, hcpcsCodes, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['HCPC S Code'],
        immediateFilterKeys: ['State'],
      },
    );

    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged) {
      this.onRequest();
    } else if (hcpcsCodes.length < count
      && this.scrollRef
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  onRequest() {
    const { actions, filters } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map(key => ({
        name: key,
        operation: key === 'State' ? 'in' : 'startswith',
        val: key === 'State' ? [filters[key]] : filters[key],
        ...key !== 'State' ? { case_sensitivity: false } : {},
      }))
      : null;
    actions.fetchHCPCSCodes({
      limit,
      offset,
      filters: requestFilters,
      triggerLoading: true,
      push: offset > 0,
    });
  }

  onFilterPrimaryButtonClick() {
    this.setState({ createDialogOpen: true });
  }

  onCreateDialogClose() {
    this.setState({ createDialogOpen: false });
  }

  onFilterSecondaryButtonClick(event) {
    this.setState({ anchorEl: event.currentTarget });
  }

  onUploadDialogClose() {
    this.setState({ uploadDialogOpen: false });
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onMenuItemClick(route) {
    this.setState({
      anchorEl: null,
      uploadDialogOpen: true,
      route,
    });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;
    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.hcpcsCodes.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const {
      hcpcsCodes,
      count,
      loading,
      account,
      filters,
    } = this.props;
    const {
      createDialogOpen,
      uploadDialogOpen,
      anchorEl,
      route,
      mobileFilterExpanded,
    } = this.state;
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Data Portability {titleSeparator} HCPCS Codes
          </title>
        </Helmet>
        <TabNavigation
          items={[
            {
              text: 'Referrals',
              href: '/settings/medical-sources/referrals',
            },
            {
              text: 'Sales',
              href: '/settings/medical-sources/sales',
            },

            {
              text: 'Prescribers',
              href: '/settings/medical-sources/prescribers',
            },
            {
              text: 'HCPCS Codes',
              href: '/settings/medical-sources/hcpc',
            },
            {
              text: 'Diagnosis',
              href: '/settings/medical-sources/diagnosis',
            },
            {
              text: 'Payers',
              href: '/settings/medical-sources/payers',
            },
          ]}
        />
        <Filter
          form="hcpcsCodesFilter"
          placeholder="Search by HCPCS Code"
          primaryButtonText="Add New Code"
          onPrimaryButtonClick={this.onFilterPrimaryButtonClick}
          secondaryButtonText="Upload File"
          onSecondaryButtonClick={this.onFilterSecondaryButtonClick}
          secondaryButtonDisabled={account.Roles.filter(item =>
            item.Name === 'Master Administrator' || item.Name === 'Administrator').length === 0}
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          showTableCount
          count={count}
          loading={loading}
          items={hcpcsCodes}
          beforeTable
          secondaryFilter={(
            <Grid item>
              <Field
                name="State"
                id="State"
                autosuggestFieldName="State Name"
                fieldArrayName="State"
                placeholder="Search State"
                component={SelectAutosuggestField}
                fullWidth
                renderSuggestionContent={suggestion => `${suggestion.name} (${suggestion.abbreviation})`}
                renderInputContent={suggestion => suggestion.abbreviation}
                onSuggestionSelected={(suggestion) => {
                  return suggestion.abbreviation;
                }}
                fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                  if (offset === 0) {
                    if (value.length > 0) {
                      resolve(usaStates.states.filter(item =>
                        item.abbreviation.indexOf(value.toUpperCase()) === 0 || item.name.toUpperCase().indexOf(value.toUpperCase()) === 0));
                    } else {
                      resolve(usaStates.states);
                    }
                  } else {
                    resolve([]);
                  }
                }))}
              />
            </Grid>
          )}
          initialValues={{
            State: 'NY',
          }}
        />
        <Menu
          id="upload-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={this.onMenuClose}
          maxHeight={130}
        >
          <MenuItem onClick={() => this.onMenuItemClick('hcpc_medicare')}>
            Medicare
          </MenuItem>
          <MenuItem onClick={() => this.onMenuItemClick('hcpc_medicaid')}>
            Medicaid
          </MenuItem>
        </Menu>
        {loading > 0 &&
          <Progress fixed />
        }
        <HCPCSCodesTable
          items={hcpcsCodes}
          count={count}
          State={filters.State}
          loading={loading}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
        <HCPCSCodeCreateEditDialog
          form="HCPCSCodeCreate"
          open={createDialogOpen}
          onClose={this.onCreateDialogClose}
          State={filters.State}
          onSubmitSuccess={() => {
            this.onCreateDialogClose();
            this.onRequest();
          }}
        />
        <UploadDialog
          title={route === 'hcpc_medicare'
            ? 'Upload Medicare Fee Schedule'
            : 'Upload Medicaid Fee Schedule'}
          subtitle="To upload list of HCPCS codes, use the template provided below:"
          route={route}
          form="HCPCSCodesUpload"
          open={uploadDialogOpen}
          onClose={this.onUploadDialogClose}
          onSubmitSuccess={() => {
            this.onUploadDialogClose();
            this.onRequest();
          }}
        />
      </Layout>
    );
  }
}

DataPortabilityHCPCSCodes.propTypes = {
  hcpcsCodes: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'HCPC S Code': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchHCPCSCodes: PropTypes.func.isRequired,
    clearHCPCSCodes: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  width: PropTypes.string.isRequired,
};

DataPortabilityHCPCSCodes.defaultProps = {};

const mapStateToProps = (store) => {
  const hcpcsCodesFilterValues = getFormValues('hcpcsCodesFilter')(store);
  return {
    hcpcsCodes: store.hcpcsCodes.data,
    count: store.hcpcsCodes.count,
    account: store.account,
    filters: {
      ...hcpcsCodesFilterValues
        && hcpcsCodesFilterValues.Filter
        ? { 'HCPC S Code': hcpcsCodesFilterValues.Filter }
        : {},
      ...hcpcsCodesFilterValues
        && hcpcsCodesFilterValues.State
        ? { 'State': hcpcsCodesFilterValues.State }
        : {},
    },
    loading: store.hcpcsCodes.loading,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchHCPCSCodes,
    clearHCPCSCodes,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withWidth()(DataPortabilityHCPCSCodes),
);



// WEBPACK FOOTER //
// ./src/pages/settings/DataPortabilityHCPCSCodes.js